"use client";
import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import {
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
} from "@percy.ai/ui";
import HvsFunnelOverlaySocialLogin from "../hvs-funnel-overlay-social-login";
import { Constants, getFormStrings } from "@/lib/constants/constants";
import { Info } from "@/lib/icons/icons";
import useSessionStorage from "@/lib/hooks/use-session-storage";

export interface HvsFunnelOverlayProps {
  randomKey: string;
  callbackUrl: string;
  subdomain?: string;
  addressToken: string;
  monthlyValuationAddress?: string;
  monthlyValuationSource?: string;
  percyValuationHigh?: string;
  type?: "inquiry" | "claim";
  defaultOpen?: boolean;
  toAgentId?: string;
  toTeamId?: string;
  agentBrandedPage?: boolean;
  delay?: number;
}

export function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
}

export function getAgentBrandedPage(value: boolean): number {
  if (value) {
    return 1;
  }
  return 0;
}

const FormSchema = z.object({
  firstName: z.string().min(2, {
    message: Constants.FIRST_NAME_VALIDATION_MESSAGE,
  }),
  lastName: z.string().min(2, {
    message: Constants.LAST_NAME_VALIDATION_MESSAGE,
  }),
  monthly: z.boolean().optional(),
  phoneNumber: z
    .string()
    .regex(
      /^(?:\+1\s?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/,
      Constants.PHONE_NUMBER_VALIDATION_MESSAGE
    )
    .optional(),
  email: z.string().email({
    message: Constants.EMAIL_VALIDATION_MESSAGE,
  }),
});

export function HvsFunnelOverlay({
  randomKey,
  subdomain = "findbuyers",
  addressToken,
  callbackUrl,
  defaultOpen = false,
  agentBrandedPage,
  toAgentId,
  toTeamId,
  monthlyValuationAddress,
  monthlyValuationSource,
  percyValuationHigh,
  type = "inquiry",
  delay = 5000,
}: HvsFunnelOverlayProps) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const [isLoading, setIsLoading] = React.useState(false);
  const [addressTokenCookie, setAddressTokenCookie] = React.useState<
    string | undefined
  >(undefined);

  const [subscribeMonthlyReport, setSubscribeMonthlyReport] = React.useState<
    boolean | "indeterminate"
  >(true);
  const [addressTokenList, addAddressToken] = useSessionStorage(
    "addressTokenList",
    ""
  );

  React.useEffect(() => {
    const showModalCookie = getCookie("address_token");
    setAddressTokenCookie(showModalCookie);
  }, [isOpen]);

  const addressTokenCookieRef = React.useRef(addressTokenCookie);
  const addressTokenListRef = React.useRef(addressTokenList);
  const addAddressTokenRef = React.useRef(addAddressToken);

  React.useEffect(() => { addressTokenCookieRef.current = addressTokenCookie; }, [addressTokenCookie]);
  React.useEffect(() => { addressTokenListRef.current = addressTokenList; }, [addressTokenList]);
  React.useEffect(() => { addAddressTokenRef.current = addAddressToken; }, [addAddressToken]);

  React.useEffect(() => {
    if (type !== "inquiry") return;

    const timer = setTimeout(() => {
      if (addressTokenListRef.current.includes(addressToken)) return;

      const cookie = getCookie("address_token");
      if (cookie && cookie === addressToken) {
        addAddressTokenRef.current(addressToken);
      } else {
        setIsOpen(true);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [type, addressToken, delay]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      monthly: true,
    },
  });

  async function claimOwnership(data: z.infer<typeof FormSchema>) {
    const formData = new FormData();
    formData.append("random_key", randomKey ?? "");
    formData.append("firstname", data.firstName);
    formData.append("lastname", data.lastName);
    formData.append("email", data.email);

    try {
      setIsLoading(true);
      const response = await fetch(`/findbuyers/email_login`, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        window.location.href = callbackUrl;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function createInquiry(data: z.infer<typeof FormSchema>) {
    const formData = new FormData();
    formData.append("random_key", randomKey ?? "");
    formData.append("firstname", data.firstName);
    formData.append("lastname", data.lastName);
    formData.append("tel", data?.phoneNumber || "");
    formData.append("email", data.email);
    formData.append("monthly_valuation_address", monthlyValuationAddress || "");
    formData.append("monthly_valuation_source", monthlyValuationSource || "");
    formData.append("percy_valuation_high", percyValuationHigh || "");
    formData.append(
      "agent_branded_page",
      `${getAgentBrandedPage(agentBrandedPage ?? false)}`
    );
    formData.append("to_agent_id", toAgentId ?? "");
    formData.append("to_team_id", toTeamId ?? "");
    formData.append("subscribe", subscribeMonthlyReport ? "1" : "0");

    try {
      setIsLoading(true);
      const response = await fetch(
        `/findbuyers/create_funnel_conversion_form`,
        {
          method: "POST",
          body: formData,
        }
      );
      if (true) {
        setIsOpen(false);
      }
    } catch (error) {
      setIsOpen(true);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (type === "inquiry") {
      createInquiry(data);
    } else {
      claimOwnership(data);
    }
  }

  function maskPhoneNumber(phoneNumber?: string) {
    if (!phoneNumber) return "";
    // Remove all non-numeric characters
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");

    // Format the cleaned number using regex
    const maskedPhoneNumber = cleaned.replace(
      /(\d{0,3})(\d{0,3})(\d{0,4})/,
      function (_, p1, p2, p3) {
        let result = "";
        if (p1) result = `(${p1}`;
        if (p1.length === 3) result += ") ";
        if (p2) result += p2;
        if (p2.length === 3) result += "-";
        if (p3) result += p3;
        return result;
      }
    );

    return maskedPhoneNumber;
  }

  function renderForm() {
    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-6"
        >
          <div className="flex flex-col gap-4 lg:flex-row">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>{Constants.FIRST_NAME_LABEL}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>{Constants.LAST_NAME_LABEL}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field: { value, ...field } }) => (
              <FormItem>
                <FormLabel>{Constants.PHONE_NUMBER_LABEL}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={value ? maskPhoneNumber(value) : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{getFormStrings(type).emailLabel}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit">
            {isLoading && <Spinner className="mr-2 h-4 w-4" />}
            {Constants.BUTTON_LABEL}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background text-muted-foreground px-2">
                {Constants.SOCIAL_SECTION_TITLE}
              </span>
            </div>
          </div>

          <HvsFunnelOverlaySocialLogin
            randomKey={randomKey}
            subdomain={subdomain}
            callbackUrl={callbackUrl}
            subscribe={subscribeMonthlyReport}
            type={type}
          />

          {type === "inquiry" && (
            <>
              <FormField
                control={form.control}
                name="monthly"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-row  items-center space-x-2 ">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(value) => {
                            field.onChange(value);
                            setSubscribeMonthlyReport(value);
                          }}
                        />
                        <FormLabel
                          className={cn("text-sm font-normal leading-5")}
                        >
                          {Constants.SUBSCRIBE_MONTHLY_REPORT}
                        </FormLabel>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <Alert variant="default">
                <Info className="h-4 w-4" />
                <AlertTitle className="mb-3">
                  {Constants.BANNER_TITLE}
                </AlertTitle>
                <AlertDescription className="text-sm text-gray-700 ">
                  {Constants.BANNER_DESCRIPTION}
                </AlertDescription>
              </Alert>
            </>
          )}
        </form>
      </Form>
    );
  }

  return type === "inquiry" ? (
    <Dialog open={isOpen}>
      <DialogContent hideClose>
        <DialogHeader>
          <DialogTitle>{getFormStrings(type).dialogTitle}</DialogTitle>
        </DialogHeader>
        {renderForm()}
      </DialogContent>
    </Dialog>
  ) : (
    <div className={cn("flex flex-col gap-2")}>
      <header>
        <h2 className={cn("text-lg font-semibold leading-none tracking-tight")}>
          {getFormStrings(type).dialogTitle}
        </h2>
      </header>
      {renderForm()}
    </div>
  );
}
